import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { INSTALL_PROMPT_SOURCE } from './install-prompt-source';

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private source = inject(INSTALL_PROMPT_SOURCE);
  private readonly standalone = signal(this.detectStandalone());

  /** True when the app is already installed (running standalone or an appinstalled fired). */
  readonly installed: Signal<boolean> = computed(
    () => this.source.installed() || this.standalone(),
  );

  /** True when the browser offered an install prompt we haven't consumed and we aren't installed. */
  readonly canInstall: Signal<boolean> = computed(
    () => this.source.event() !== null && !this.installed(),
  );

  /** Show the native install prompt. A captured prompt is single-use, so it is consumed first. */
  async promptInstall(): Promise<void> {
    const evt = this.source.event();
    if (!evt) return;
    this.source.event.set(null);
    await evt.prompt();
    await evt.userChoice;
  }

  private detectStandalone(): boolean {
    if (typeof window === 'undefined') return false;
    const displayMode = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
    const iosStandalone =
      (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    return displayMode || iosStandalone;
  }
}
